import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import LanguageSwitcher from '../components/LanguageSwitcher'
import DarkModeToggle from '../components/DarkModeToggle'
import SEO from '../components/SEO'

function Impressum() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Generate Organization Schema.org structured data
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: t('impressum.companyName'),
    address: {
      '@type': 'PostalAddress',
      streetAddress: t('impressum.street'),
      addressLocality: t('impressum.cityZip').split(' ')[1] || 'Musterstadt',
      postalCode: t('impressum.cityZip').split(' ')[0] || '12345',
      addressCountry: 'DE',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+49-123-456789',
      email: 'apps@vanhulst.de',
      contactType: 'customer service',
    },
    url: window.location.origin,
    vatID: 'DE123456789',
  }

  return (
    <>
      <SEO page="impressum" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <div className="min-h-screen flex flex-col">
        {/* Controls - Top Right */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <DarkModeToggle />
        <LanguageSwitcher />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-start justify-center p-4 pt-20">
        <div className="w-full max-w-4xl">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 transition-colors">
            {/* Back Button */}
            <button
              onClick={() => navigate('/')}
              className="mb-6 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold flex items-center gap-2"
            >
              ← {t('common.back')}
            </button>

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold mb-4 dark:text-white">
                {t('impressum.title')}
              </h1>
            </div>

            {/* Content */}
            <div className="prose dark:prose-invert max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('impressum.provider')}
                </h2>
                <div className="text-gray-700 dark:text-gray-300 space-y-1">
                  <p>{t('impressum.companyName')}</p>
                  <p>{t('impressum.street')}</p>
                  <p>{t('impressum.cityZip')}</p>
                  <p>{t('impressum.country')}</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('impressum.contact')}
                </h2>
                <div className="text-gray-700 dark:text-gray-300 space-y-1">
                  <p>{t('impressum.phone')}: +49 (0) 123 456789</p>
                  <p>{t('impressum.email')}: info@example.com</p>
                  <p>{t('impressum.website')}: www.example.com</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('impressum.representative')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  {t('impressum.representativeName')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('impressum.registrationTitle')}
                </h2>
                <div className="text-gray-700 dark:text-gray-300 space-y-1">
                  <p>{t('impressum.registrationCourt')}: Amtsgericht Musterstadt</p>
                  <p>{t('impressum.registrationNumber')}: HRB 12345</p>
                  <p>{t('impressum.vatId')}: DE123456789</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('impressum.responsibleForContent')}
                </h2>
                <div className="text-gray-700 dark:text-gray-300">
                  <p>{t('impressum.responsibleName')}</p>
                  <p>{t('impressum.street')}</p>
                  <p>{t('impressum.cityZip')}</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('impressum.disputeResolution')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  {t('impressum.disputeText')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                  {t('impressum.liability')}
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('impressum.liabilityContent')}
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  {t('impressum.liabilityLinks')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default Impressum
